import React, { useState, useEffect, useRef } from 'react';
import {
  X,
  Plus,
  Trash2,
  Clock,
  FileText,
  FileSignature,
  Mail,
  Calendar,
  CreditCard,
  FileSpreadsheet,
  Ticket,
  CheckSquare,
  Tag,
  GitCommit,
  FolderPlus,
  ShieldCheck,
  AlertTriangle,
  RefreshCw,
} from 'lucide-react';
import { AutomationActionType, AutomationNodeData, DelayUnit } from '../../../types/automation';
import { safeFetch } from '../../../lib/api';
import { resolveMergeFields } from '../../../lib/variables/resolver';
import { joinPersonName, sanitizeNamePart } from '../../../lib/nameUtils';
import { sanitizeEmailHtml } from '../email/emailSanitize';
import DocumentTemplateMultiSelect from '../../shared/DocumentTemplateMultiSelect';
import SearchableSelect from '../../shared/SearchableSelect';

// Shaped like a real application record, because that is what the shared
// resolver reads: names from personalDetails, email from userEmail.
// (The flat keys are kept for any token that reads them directly.)
const SAMPLE_APPLICANT_PREVIEW = {
  personalDetails: { firstName: 'Jane', lastName: 'Doe' },
  userEmail: 'jane.doe@example.com',
  applicantName: 'Jane Doe',
  firstName: 'Jane',
  lastName: 'Doe',
  email: 'jane.doe@example.com',
  applicationId: 'APP-10119',
  programName: 'Global Career Internship Program',
  program: 'Global Career Internship Program',
  programType: 'Global Career Internship Program',
  stage: 'Processing',
  status: 'In Progress',
  workspaceName: 'ISATS Portal',
};

interface AutomationNodeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: AutomationNodeData) => void;
  initialData?: AutomationNodeData;
}

const ACTION_TYPES: Array<{
  type: AutomationActionType;
  label: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
}> = [
  { type: 'generate_document', label: 'Generate Document', description: 'Create document using Template Generator', icon: FileText },
  { type: 'send_sign_request', label: 'Send Sign Request', description: 'Send built-in native e-signature request', icon: FileSignature },
  { type: 'send_email', label: 'Send Email', description: 'Dispatch email with dynamic variables', icon: Mail },
  { type: 'create_event', label: 'Create Event', description: 'Schedule appointment/event on calendar', icon: Calendar },
  { type: 'create_invoice', label: 'Create & Send Invoice', description: 'Issue an invoice from a Finance template', icon: CreditCard },
  { type: 'create_quote', label: 'Create & Send Quote', description: 'Issue a quote from a Finance template', icon: FileSpreadsheet },
  { type: 'create_support_ticket', label: 'Create Support Ticket', description: 'File issue in Support Desk', icon: Ticket },
  { type: 'create_internal_task', label: 'Create Internal Task', description: 'Assign task to team member', icon: CheckSquare },
  { type: 'tag_application', label: 'Tag Application', description: 'Add or remove application tags', icon: Tag },
  { type: 'change_workflow_stage', label: 'Change Workflow Stage', description: 'Advance or set pipeline stage', icon: GitCommit },
  { type: 'update_application_status', label: 'Update Application Status', description: 'Change main application status (Draft, Submitted, Returned, Cancelled, Closed, Archived)', icon: RefreshCw },
  { type: 'update_batch', label: 'Update Batch', description: 'Add applicants to a batch group, or replace its members', icon: FolderPlus },
  { type: 'update_admin', label: 'Update Admin', description: 'Modify or remove a workspace team administrator', icon: ShieldCheck },
];

// Slice B: tag lists are kept deduplicated case-insensitively with
// first-seen casing preserved — in the picker state, in what the
// multi-select adds, and in the saved config. The engine normalizes
// again at execution time (defense in depth).
const coerceTagArray = (raw: unknown): string[] =>
  Array.isArray(raw)
    ? raw.map((t) => String(t))
    : typeof raw === 'string'
      ? raw.split(',').map((t) => t.trim()).filter(Boolean)
      : [];

const dedupeTagsCi = (tags: string[]): string[] => {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of tags) {
    const tag = String(raw).trim();
    if (!tag) continue;
    const key = tag.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(tag);
  }
  return out;
};

interface TagChipInputProps {
  tags: string[];
  onChange: (tags: string[]) => void;
  placeholder?: string;
  badgeBgColor?: string;
}

const TagChipInput: React.FC<TagChipInputProps> = ({
  tags = [],
  onChange,
  placeholder = 'Type a tag and press comma or Enter...',
  badgeBgColor = 'bg-indigo-50 border-indigo-200 text-indigo-700',
}) => {
  const [inputValue, setInputValue] = useState('');
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editingValue, setEditingValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const currentTags = Array.isArray(tags)
    ? tags.map((t) => String(t).trim()).filter(Boolean)
    : typeof tags === 'string'
      ? (tags as string).split(',').map((t) => t.trim()).filter(Boolean)
      : [];

  const processAndAddTags = (text: string) => {
    if (!text) return;
    const parts = text.split(',').map((t) => t.trim()).filter(Boolean);
    if (parts.length === 0) return;

    const nextTags = [...currentTags];
    for (const part of parts) {
      if (!nextTags.includes(part)) {
        nextTags.push(part);
      }
    }
    onChange(nextTags);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    if (val.includes(',')) {
      const parts = val.split(',');
      const toAdd = parts.slice(0, -1).join(',');
      processAndAddTags(toAdd);
      setInputValue(parts[parts.length - 1]);
    } else {
      setInputValue(val);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === ',' || e.key === 'Enter') {
      e.preventDefault();
      if (inputValue.trim()) {
        processAndAddTags(inputValue);
        setInputValue('');
      }
    } else if (e.key === 'Backspace' && !inputValue && currentTags.length > 0) {
      const nextTags = currentTags.slice(0, -1);
      onChange(nextTags);
    }
  };

  const handleBlur = () => {
    if (inputValue.trim()) {
      processAndAddTags(inputValue);
      setInputValue('');
    }
  };

  const removeTag = (index: number) => {
    const nextTags = currentTags.filter((_, i) => i !== index);
    onChange(nextTags);
  };

  const startEditTag = (index: number) => {
    setEditingIndex(index);
    setEditingValue(currentTags[index]);
  };

  const saveEditTag = (index: number) => {
    if (editingIndex === null) return;
    const trimmed = editingValue.trim().replace(/,/g, '');
    if (trimmed) {
      const nextTags = [...currentTags];
      nextTags[index] = trimmed;
      onChange(nextTags);
    } else {
      removeTag(index);
    }
    setEditingIndex(null);
    setEditingValue('');
  };

  return (
    <div
      onClick={() => inputRef.current?.focus()}
      className="w-full min-h-[42px] px-3 py-2 rounded-xl border border-slate-200 bg-white focus-within:ring-2 focus-within:ring-indigo-500/20 focus-within:border-indigo-500 flex flex-wrap items-center gap-1.5 cursor-text transition-all"
    >
      {currentTags.map((tag, idx) => {
        if (editingIndex === idx) {
          return (
            <input
              key={idx}
              type="text"
              autoFocus
              value={editingValue}
              onChange={(e) => setEditingValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ',') {
                  e.preventDefault();
                  saveEditTag(idx);
                } else if (e.key === 'Escape') {
                  setEditingIndex(null);
                }
              }}
              onBlur={() => saveEditTag(idx)}
              className="px-2 py-0.5 text-xs font-bold border border-indigo-400 rounded-md outline-none bg-indigo-50 text-indigo-900 min-w-[60px]"
            />
          );
        }
        return (
          <span
            key={idx}
            className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg border text-xs font-bold transition-all ${badgeBgColor}`}
          >
            <span
              onClick={(e) => {
                e.stopPropagation();
                startEditTag(idx);
              }}
              className="cursor-pointer hover:underline"
              title="Click to edit tag"
            >
              {tag}
            </span>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                removeTag(idx);
              }}
              className="p-0.5 rounded-full hover:bg-black/10 transition-colors cursor-pointer"
              title="Remove tag"
            >
              <X size={12} />
            </button>
          </span>
        );
      })}

      <input
        ref={inputRef}
        type="text"
        value={inputValue}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        placeholder={currentTags.length === 0 ? placeholder : 'Type next tag...'}
        className="flex-1 min-w-[140px] text-xs font-medium text-slate-800 bg-transparent outline-none py-0.5 placeholder:text-slate-400"
      />
    </div>
  );
};

export const AutomationNodeModal: React.FC<AutomationNodeModalProps> = ({
  isOpen,
  onClose,
  onSave,
  initialData,
}) => {
  const [label, setLabel] = useState(initialData?.label || 'New Action');
  const [actionType, setActionType] = useState<AutomationActionType>(
    initialData?.actionType || 'generate_document'
  );
  const [delayValue, setDelayValue] = useState<number>(initialData?.delayValue || 0);
  const [delayUnit, setDelayUnit] = useState<DelayUnit>(initialData?.delayUnit || 'hours');
  const [config, setConfig] = useState<Record<string, any>>(initialData?.config || {});
  const [saveError, setSaveError] = useState<string | null>(null);

  // Dropdown option states
  const [templates, setTemplates] = useState<Array<{ id: string; name: string }>>([]);
  const [signingTemplates, setSigningTemplates] = useState<Array<{ id: string; name: string; documentName?: string }>>([]);
  const [emailTemplates, setEmailTemplates] = useState<
    Array<{ id: string; name: string; documentTemplateIds?: string[] }>
  >([]);
  const [invoiceTemplates, setInvoiceTemplates] = useState<Array<{ id: string; name: string }>>([]);
  const [batches, setBatches] = useState<Array<{ id: string; name: string }>>([]);
  // Slice B: the distinct tags currently used across this workspace's
  // applications (GET /api/admin/automations/application-tags), offered
  // as a multi-select alongside the free-text chip entry.
  const [availableTags, setAvailableTags] = useState<string[]>([]);
  // Slice C: the workspace TEAM directory + assignable roles for the
  // Update Admin picker (GET /api/admin/automations/workspace-admins).
  // The endpoint is settings-gated, so for roles without settings
  // access it 403s and both lists stay empty — the config block then
  // explains the permission instead of rendering a dead picker.
  const [workspaceAdmins, setWorkspaceAdmins] = useState<
    Array<{ uid: string; name: string; email: string; role: string }>
  >([]);
  const [assignableRoles, setAssignableRoles] = useState<string[]>([]);
  const [removalConfirmOpen, setRemovalConfirmOpen] = useState(false);
  const [teamMembers, setTeamMembers] = useState<Array<{ id: string; name: string }>>([]);
  // A picker list that failed to load must say so — an empty dropdown used
  // to mean both "no templates exist" and "the request was rejected".
  const [pickerError, setPickerError] = useState<string | null>(null);

  useEffect(() => {
    setSaveError(null);
    if (initialData) {
      setLabel(initialData.label);
      // Slice A: Add to Batch was renamed Update Batch. A stored
      // add_to_batch node opens AS update_batch with the Append
      // operation (its exact historical behavior); if the user saves,
      // the node is stored under the new type — the stored automation
      // is only ever rewritten by this explicit edit, never migrated
      // in the background.
      const isLegacyBatchNode = initialData.actionType === 'add_to_batch';
      setActionType(isLegacyBatchNode ? 'update_batch' : initialData.actionType);
      setDelayValue(initialData.delayValue || 0);
      setDelayUnit(initialData.delayUnit || 'hours');
      setConfig(
        isLegacyBatchNode
          ? {
              ...(initialData.config || {}),
              batchOperation:
                initialData.config?.batchOperation === 'replace_all'
                  ? 'replace_all'
                  : 'append',
            }
          : initialData.config || {}
      );
    } else {
      setLabel('New Action');
      setActionType('generate_document');
      setDelayValue(0);
      setDelayUnit('hours');
      setConfig({});
    }
  }, [initialData, isOpen]);

  const loadPickers = async () => {
    // safeFetch attaches the auth token + workspace header and throws on
    // non-OK responses; failures surface via pickerError instead of
    // silently rendering empty dropdowns.
    const [docRes, signTplRes, emailRes, invoiceRes, batchRes, usersRes, tagsRes, adminsRes] = await Promise.allSettled([
      safeFetch('/api/admin/document-templates'),
      safeFetch('/api/signing-templates'),
      safeFetch('/api/email-templates'),
      safeFetch('/api/admin/finance/invoice-templates'),
      safeFetch('/api/admin/batches'),
      safeFetch('/api/admin/users'),
      safeFetch('/api/admin/automations/application-tags'),
      safeFetch('/api/admin/automations/workspace-admins'),
    ]);

    if (adminsRes.status === 'fulfilled') {
      const rawAdmins = Array.isArray(adminsRes.value?.admins) ? adminsRes.value.admins : [];
      setWorkspaceAdmins(
        rawAdmins
          .map((a: any) => ({
            uid: String(a?.uid || ''),
            name: String(a?.name || a?.email || a?.uid || ''),
            email: String(a?.email || ''),
            role: String(a?.role || ''),
          }))
          .filter((a: { uid: string }) => a.uid)
      );
      const rawRoles = Array.isArray(adminsRes.value?.roles) ? adminsRes.value.roles : [];
      setAssignableRoles(rawRoles.map((r: any) => String(r)).filter(Boolean));
    }

    if (tagsRes.status === 'fulfilled') {
      const rawTags = Array.isArray(tagsRes.value?.tags)
        ? tagsRes.value.tags
        : Array.isArray(tagsRes.value)
          ? tagsRes.value
          : [];
      setAvailableTags(dedupeTagsCi(rawTags.map((t: any) => String(t))));
    }

    if (usersRes.status === 'fulfilled') {
      const rawUsers = Array.isArray(usersRes.value.users)
        ? usersRes.value.users
        : Array.isArray(usersRes.value)
          ? usersRes.value
          : [];
      const externalRoles = new Set(['applicant', 'candidate', 'job_seeker', 'employer', 'referrer', 'sponsor']);
      const teamList = rawUsers
        .filter((u: any) => {
          const r = String(u.workspaceRole || u.role || u.primaryRole || '').toLowerCase();
          return r && !externalRoles.has(r);
        })
        .map((u: any) => {
          const name = sanitizeNamePart(u.displayName) || joinPersonName([u.firstName, u.lastName]) || u.email || 'Team Member';
          const role = String(u.workspaceRole || u.role || u.primaryRole || 'Member').toLowerCase();
          return {
            id: String(u.uid || u.id || ''),
            name: `${name} (${role.charAt(0).toUpperCase() + role.slice(1)})`,
          };
        })
        .filter((u: { id: string }) => u.id);
      setTeamMembers(teamList);
    }

    if (docRes.status === 'fulfilled' && Array.isArray(docRes.value.templates)) {
      setTemplates(docRes.value.templates);
    }
    if (signTplRes.status === 'fulfilled') {
      const rawSignTpls = Array.isArray(signTplRes.value.templates)
        ? signTplRes.value.templates
        : Array.isArray(signTplRes.value)
          ? signTplRes.value
          : [];
      setSigningTemplates(
        rawSignTpls.map((t: any) => ({
          id: String(t.id || ''),
          name: String(t.name || t.documentName || 'Signing Template'),
          documentName: String(t.documentName || ''),
        })).filter((t: any) => t.id)
      );
    }
    if (emailRes.status === 'fulfilled') {
      if (Array.isArray(emailRes.value.templates)) setEmailTemplates(emailRes.value.templates);
      else if (Array.isArray(emailRes.value)) setEmailTemplates(emailRes.value);
    }
    if (invoiceRes.status === 'fulfilled' && Array.isArray(invoiceRes.value.templates)) {
      setInvoiceTemplates(invoiceRes.value.templates);
    }
    if (batchRes.status === 'fulfilled') {
      // GET /api/admin/batches identifies each batch by its `batchId`
      // field (the "BAT-10122"-style public identifier for legacy
      // batches) — the items carry NO `id` property. The picker state
      // is typed { id, name } and the option values read `b.id`, so
      // without this mapping every option value was undefined and a
      // chosen batch never landed in config.batchId: the Add to Batch
      // action could not be configured with any batch at all.
      const rawBatches = Array.isArray(batchRes.value.batches)
        ? batchRes.value.batches
        : Array.isArray(batchRes.value)
          ? batchRes.value
          : null;
      if (rawBatches) {
        setBatches(
          rawBatches
            .map((b: any) => {
              const bId = String(b?.batchId ?? b?.id ?? '');
              const rawName = String(b?.name || '');
              const displayId = bId.startsWith('BAT-') ? bId : (b?.numericId ? `BAT-${b.numericId}` : bId);
              const displayName = rawName ? (rawName.startsWith('BAT-') ? rawName : `${displayId} - ${rawName}`) : displayId;
              return {
                id: bId,
                name: displayName,
              };
            })
            .filter((b: { id: string }) => b.id)
        );
      }
    }

    const firstFailure = [docRes, emailRes, invoiceRes, batchRes].find(
      (r) => r.status === 'rejected'
    ) as PromiseRejectedResult | undefined;
    setPickerError(
      firstFailure ? firstFailure.reason?.message || 'Failed to load templates' : null
    );
  };

  useEffect(() => {
    if (!isOpen) return;
    loadPickers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  if (!isOpen) return null;

  // Template-only finance actions (owner decision, 2026-10-16): new
  // invoice/quote steps are configured from a Finance template — the
  // hand-typed amount/items path is gone from the builder. Steps saved
  // BEFORE that decision with manual line items are "legacy manual"
  // setups: they keep running exactly as saved (the engine still
  // honors them and publish validation still accepts them), they show
  // a notice here, and picking a template converts the step — the
  // manual config is never silently dropped from the saved node.
  const invoiceIsLegacyManual =
    !config.invoiceTemplateId && Array.isArray(config.lineItems) && config.lineItems.length > 0;
  const quoteTemplateId = config.quoteTemplateId || config.invoiceTemplateId || '';
  const quoteIsLegacyManual =
    !quoteTemplateId && Array.isArray(config.quoteLineItems) && config.quoteLineItems.length > 0;

  const handleSave = () => {
    if (actionType === 'send_sign_request' && !config.signingTemplateId) {
      setSaveError('Choose a signing template for the Send Sign Request step.');
      return;
    }
    if (actionType === 'create_invoice' && !config.invoiceTemplateId && !invoiceIsLegacyManual) {
      setSaveError(
        'Choose a Finance invoice template — invoice steps are built from templates now. (Steps saved earlier with manual items keep working; this one has neither.)'
      );
      return;
    }
    if (actionType === 'create_quote' && !quoteTemplateId && !quoteIsLegacyManual) {
      setSaveError(
        'Choose a Finance quote template — quote steps are built from templates now. (Steps saved earlier with manual items keep working; this one has neither.)'
      );
      return;
    }
    if (actionType === 'update_application_status' && !config.targetApplicationStatus) {
      setSaveError('Select a target Application Status for this step.');
      return;
    }
    // Remove Member (D-4) needs no target batch — every other batch
    // operation (and the legacy alias) still refuses to save without one.
    const isRemoveMemberStep =
      actionType === 'update_batch' && config.batchOperation === 'remove_member';
    if ((actionType === 'update_batch' || actionType === 'add_to_batch') && !config.batchId && !isRemoveMemberStep) {
      setSaveError('Choose a target batch group for the Update Batch step.');
      return;
    }
    if (actionType === 'update_admin') {
      // Slice C: client validation mirrors the backend — an invalid
      // Update Admin config cannot be saved here, and the backend
      // re-checks everything at save, publish and execution time. The
      // EFFECTIVE field/value are what the controls display (the
      // selects render defaults for unset config), and those are what
      // get validated and saved below — a default the UI shows must
      // never be absent from the stored config.
      const op = config.adminOperation || 'set_to';
      if (!config.adminTargetUid) {
        setSaveError('Choose the workspace team administrator this step applies to.');
        return;
      }
      if (op === 'remove_admin') {
        if (config.adminRemovalConfirmed !== true) {
          setSaveError(
            'Review and confirm the removal first — the confirmation names the administrator and is required before a Remove Admin step can be saved.'
          );
          return;
        }
      } else if (op === 'set_to') {
        const f = config.adminField || 'displayName';
        const v =
          f === 'status'
            ? config.adminValue ?? 'active'
            : f === 'screeningStaff'
              ? config.adminValue ?? true
              : config.adminValue;
        if ((f === 'displayName' || f === 'firstName' || f === 'lastName') && (typeof v !== 'string' || !v.trim())) {
          setSaveError('Enter the new value for the field this step sets.');
          return;
        }
        if (f === 'role' && (typeof v !== 'string' || !v.trim())) {
          setSaveError('Choose the workspace role this step assigns.');
          return;
        }
      }
    }
    setSaveError(null);
    // Slice A: an Update Batch node always stores its operation
    // explicitly (absent would also mean Append to the engine, but the
    // saved automation should say what it does).
    const isBatchNode = actionType === 'update_batch' || actionType === 'add_to_batch';
    let savedConfig = config;
    if (isBatchNode) {
      // PR #1333 review fix: this normalization previously rewrote
      // EVERY non-replace_all operation to 'append', which silently
      // turned a saved Remove Member step into an Append — the
      // operation now round-trips, and Remove Member stores no batch
      // reference at all (the engine ignores one for it, and a stale
      // name must never label the step).
      const operation =
        config.batchOperation === 'replace_all'
          ? 'replace_all'
          : config.batchOperation === 'remove_member'
            ? 'remove_member'
            : 'append';
      savedConfig = {
        ...savedConfig,
        batchOperation: operation,
        ...(operation === 'remove_member' ? { batchId: '', batchName: '' } : {}),
      };
    }
    // Slice B: a saved tag config is always the deduplicated union
    // (dropdown-selected + typed), case-insensitive with first-seen
    // casing — even for a config that arrived unnormalized.
    if (actionType === 'tag_application') {
      savedConfig = {
        ...savedConfig,
        tagsToAdd: dedupeTagsCi(coerceTagArray(config.tagsToAdd)),
        tagsToRemove: dedupeTagsCi(coerceTagArray(config.tagsToRemove)),
      };
    }
    // Slice C: an Update Admin config is saved in its canonical
    // shape — the operation and (for Set To) the field/value are
    // materialized with the same defaults the controls display,
    // Append always means the screeningStaff assignment, and the
    // target's display name is stamped for the canvas/dry-run labels
    // (execution always re-reads the live record).
    if (actionType === 'update_admin') {
      const op = config.adminOperation || 'set_to';
      const targetAdmin = workspaceAdmins.find((a) => a.uid === config.adminTargetUid);
      const effField = op === 'append' ? 'screeningStaff' : config.adminField || 'displayName';
      savedConfig = {
        ...savedConfig,
        adminOperation: op,
        adminTargetName: targetAdmin?.name || config.adminTargetName || '',
        ...(op === 'append'
          ? { adminField: 'screeningStaff', adminValue: true }
          : op === 'remove_admin'
            ? { adminField: undefined, adminValue: undefined }
            : {
                adminField: effField,
                adminValue:
                  effField === 'status'
                    ? config.adminValue ?? 'active'
                    : effField === 'screeningStaff'
                      ? config.adminValue ?? true
                      : config.adminValue,
              }),
      };
    }
    onSave({
      label,
      actionType: isBatchNode ? 'update_batch' : actionType,
      delayValue,
      delayUnit,
      config: savedConfig,
    });
    onClose();
  };

  return (
    <div
      onWheel={(e) => e.stopPropagation()}
      className="fixed inset-0 z-[9500] flex items-center justify-center bg-slate-900/60 backdrop-blur-xs p-4 overflow-y-auto"
    >
      <div
        onWheel={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden animate-scale-in"
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between bg-slate-50 shrink-0">
          <div>
            <h3 className="text-base font-extrabold text-slate-900">Configure Action Node</h3>
            <p className="text-xs text-slate-500">Define properties and settings for this automation step.</p>
          </div>
          <button aria-label="Close" title="Close"
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-200/50 transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        {/* Form Body */}
        <div className="p-6 overflow-y-auto space-y-6 flex-1">
          {/* Template/batch picker load failure — distinct from empty lists */}
          {pickerError && (
            <div className="flex items-center justify-between gap-3 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-xs font-semibold text-rose-700">
              <span>Couldn't load templates — {pickerError} Pickers below may be incomplete.</span>
              <button
                type="button"
                onClick={loadPickers}
                className="px-3 py-1.5 rounded-lg bg-white border border-rose-200 text-rose-700 font-bold hover:bg-rose-100 transition-colors cursor-pointer shrink-0"
              >
                Retry
              </button>
            </div>
          )}

          {/* Action Name */}
          <div>
            <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-2">
              Step Label
            </label>
            <input
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="e.g. Issue Welcome Invoice"
              className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-brand-primary text-sm font-semibold text-slate-800"
            />
          </div>

          {/* Action Type Selector */}
          <div>
            <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-2">
              Action Type
            </label>
            <div
              onWheel={(e) => e.stopPropagation()}
              className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 max-h-48 overflow-y-auto p-1 border border-slate-100 rounded-xl bg-slate-50 overscroll-contain"
            >
              {ACTION_TYPES.map((a) => {
                const Icon = a.icon;
                const isSelected = actionType === a.type;
                return (
                  <button
                    key={a.type}
                    type="button"
                    onClick={() => {
                      setActionType(a.type);
                      if (!label || label === 'New Action') setLabel(a.label);
                    }}
                    className={`flex items-start gap-3 p-3 rounded-xl text-left border transition-all ${
                      isSelected
                        ? 'bg-indigo-50 border-indigo-500 ring-1 ring-indigo-500/20'
                        : 'bg-white border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <div
                      className={`p-2 rounded-lg shrink-0 ${
                        isSelected ? 'bg-brand-primary text-white' : 'bg-slate-100 text-slate-600'
                      }`}
                    >
                      <Icon className="h-4 w-4" />
                    </div>
                    <div>
                      <p className="text-xs font-bold text-slate-800">{a.label}</p>
                      <p className="text-[11px] text-slate-500 leading-tight">{a.description}</p>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Delay Settings */}
          <div className="bg-amber-50/60 border border-amber-200/80 rounded-xl p-4 space-y-2">
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-amber-600" />
              <label className="text-xs font-bold text-amber-900 uppercase tracking-wider">
                Execution Delay (Wait before executing)
              </label>
            </div>
            <div className="flex items-center gap-3">
              <input
                type="number"
                min="0"
                value={delayValue}
                onChange={(e) => setDelayValue(Math.max(0, parseInt(e.target.value) || 0))}
                className="w-28 px-3 py-1.5 rounded-lg border border-amber-300 bg-white text-sm font-bold text-slate-800 focus:outline-none focus:ring-2 focus:ring-amber-500"
              />
              <select
                value={delayUnit}
                onChange={(e) => setDelayUnit(e.target.value as DelayUnit)}
                className="px-3 py-1.5 rounded-lg border border-amber-300 bg-white text-sm font-semibold text-slate-800 focus:outline-none focus:ring-2 focus:ring-amber-500"
              >
                <option value="minutes">Minutes</option>
                <option value="hours">Hours</option>
                <option value="days">Days</option>
              </select>
              <span className="text-xs text-amber-800 font-medium">
                {delayValue === 0 ? 'Executes immediately' : `Executes ${delayValue} ${delayUnit} after previous step`}
              </span>
            </div>
          </div>

          {/* Specific Configuration Controls */}
          <div className="border-t border-slate-100 pt-4 space-y-4">
            <h4 className="text-xs font-bold text-slate-800 uppercase tracking-wider">Action Parameters</h4>

            {actionType === 'generate_document' && (
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">Document Template</label>
                  <SearchableSelect
                    value={config.templateId || ''}
                    onChange={(v) => setConfig({ ...config, templateId: v })}
                    options={templates.map((t) => ({ value: t.id, label: t.name }))}
                    placeholder="Search document templates..."
                    clearLabel="Select Document Template..."
                    emptyLabel="No document templates found"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="saveToApplicantDocs"
                    checked={config.saveToApplicantDocs !== false}
                    onChange={(e) => setConfig({ ...config, saveToApplicantDocs: e.target.checked })}
                    className="rounded text-indigo-600 focus:ring-brand-primary h-4 w-4"
                  />
                  <label htmlFor="saveToApplicantDocs" className="text-xs font-medium text-slate-700">
                    Save generated PDF to reference applicant's Documents section
                  </label>
                </div>
              </div>
            )}

            {actionType === 'send_sign_request' && (
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    Signing Template <span className="text-rose-600">*</span>
                  </label>
                  <SearchableSelect
                    value={config.signingTemplateId || ''}
                    onChange={(v) => {
                      const selected = signingTemplates.find((t) => t.id === v);
                      setConfig({
                        ...config,
                        signingTemplateId: v,
                        signingTemplateName: selected?.name || '',
                      });
                    }}
                    options={signingTemplates.map((t) => ({ value: t.id, label: t.name }))}
                    placeholder="Search signing templates..."
                    clearLabel="Select Signing Template..."
                    emptyLabel="No signing templates found"
                  />
                  <p className="text-[11px] text-slate-500 mt-1">
                    Choose a native built-in e-signature PDF template with mapped signing fields.
                  </p>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">Signing Order / Mode</label>
                  <select
                    value={config.signingMode || 'any_order'}
                    onChange={(e) => setConfig({ ...config, signingMode: e.target.value as 'any_order' | 'strict_order' })}
                    className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm font-semibold text-slate-800 focus:outline-none focus:ring-2 focus:ring-brand-primary/20"
                  >
                    <option value="any_order">Any Order — all signers receive invitation immediately</option>
                    <option value="strict_order">Strict Order — signers receive invitation sequentially in slot order</option>
                  </select>
                </div>

                <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 space-y-3">
                  <p className="text-[11px] font-bold text-slate-600 uppercase tracking-wider">Recipient Options</p>
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1">
                      Recipient Email Override (optional)
                    </label>
                    <input
                      type="text"
                      value={config.signerEmail || ''}
                      onChange={(e) => setConfig({ ...config, signerEmail: e.target.value })}
                      placeholder="Leave empty to send to reference applicant"
                      className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm bg-white"
                    />
                    <p className="text-[11px] text-slate-500 mt-1">
                      If left empty, the e-signature request will be addressed and emailed to the reference applicant.
                    </p>
                  </div>

                  <div className="flex items-center gap-2 pt-1">
                    <input
                      type="checkbox"
                      id="sendCompletedCopyToSigners"
                      checked={config.sendCompletedCopyToSigners === true}
                      onChange={(e) => setConfig({ ...config, sendCompletedCopyToSigners: e.target.checked })}
                      className="rounded text-indigo-600 focus:ring-brand-primary h-4 w-4"
                    />
                    <label htmlFor="sendCompletedCopyToSigners" className="text-xs font-medium text-slate-700">
                      Email completed PDF copy to all signing parties upon finalization
                    </label>
                  </div>
                </div>
              </div>
            )}

            {actionType === 'send_email' && (() => {
              const selectedTemplate: any = emailTemplates.find((t) => t.id === config.emailTemplateId);
              // Mirror the engine's send-time resolution exactly (config
              // first, template as fallback) so the preview shows what
              // will actually be sent — e.g. after an admin edits the
              // subject of a node that carries a template.
              const rawSubject = config.subject || selectedTemplate?.subject || selectedTemplate?.name || '';
              const rawBody = config.bodyHtml || selectedTemplate?.htmlBody || selectedTemplate?.bodyHtml || selectedTemplate?.content || selectedTemplate?.html || selectedTemplate?.body || '';
              const previewSubject = resolveMergeFields(rawSubject, SAMPLE_APPLICANT_PREVIEW);
              const previewBodyHtml = resolveMergeFields(rawBody, SAMPLE_APPLICANT_PREVIEW);

              return (
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1">Email Template</label>
                    <SearchableSelect
                      value={config.emailTemplateId || ''}
                      onChange={(emailTemplateId) => {
                        const next: Record<string, any> = { ...config, emailTemplateId };
                        const current: string[] = Array.isArray(config.documentTemplateIds)
                          ? config.documentTemplateIds
                          : [];
                        const chosen: any = emailTemplates.find((t) => t.id === emailTemplateId);
                        if (chosen) {
                          if (chosen.subject) {
                            next.subject = chosen.subject;
                          }
                          const tBody = chosen.htmlBody || chosen.bodyHtml || chosen.content || chosen.html || chosen.body;
                          if (tBody) {
                            next.bodyHtml = tBody;
                          }
                          if (current.length === 0 && Array.isArray(chosen.documentTemplateIds) && chosen.documentTemplateIds.length > 0) {
                            next.documentTemplateIds = [...chosen.documentTemplateIds];
                          }
                        } else {
                          next.emailTemplateId = '';
                        }
                        setConfig(next);
                      }}
                      options={emailTemplates.map((t: any) => ({
                        value: t.id,
                        label: `${t.code ? `[${t.code}] ` : ''}${t.name}`,
                      }))}
                      placeholder="Search email templates..."
                      clearLabel="-- None / Custom Email --"
                      emptyLabel="No email templates found"
                    />
                  </div>

                  {config.emailTemplateId ? (
                    <div className="rounded-xl border border-indigo-200 bg-indigo-50/50 p-4 space-y-3">
                      <div className="flex items-center justify-between border-b border-indigo-100 pb-2">
                        <span className="text-xs font-extrabold uppercase tracking-wider text-indigo-900 flex items-center gap-1.5">
                          <Mail className="h-3.5 w-3.5 text-indigo-600" /> Template Live Preview (Sample Data)
                        </span>
                        <span className="text-[10px] font-bold text-indigo-700 bg-white border border-indigo-200 px-2 py-0.5 rounded-full">
                          {selectedTemplate?.name || 'Selected Template'}
                        </span>
                      </div>

                      <div>
                        <span className="text-[10px] font-extrabold uppercase text-slate-400 block mb-0.5">Subject</span>
                        <p className="text-xs font-bold text-slate-800 bg-white border border-indigo-100 rounded-lg px-3 py-2">
                          {previewSubject || <span className="text-slate-400 font-normal">No subject defined in template</span>}
                        </p>
                      </div>

                      <div>
                        <span className="text-[10px] font-extrabold uppercase text-slate-400 block mb-0.5">Body Preview</span>
                        <div
                          className="text-xs text-slate-700 bg-white border border-indigo-100 rounded-lg p-3 max-h-48 overflow-y-auto prose prose-xs"
                          dangerouslySetInnerHTML={{ __html: sanitizeEmailHtml(previewBodyHtml || '<p class="text-slate-400 italic">No body content defined in template</p>') }}
                        />
                      </div>
                    </div>
                  ) : (
                    <>
                      <div>
                        <label className="block text-xs font-semibold text-slate-700 mb-1">Email Subject</label>
                        <input
                          type="text"
                          value={config.subject || ''}
                          onChange={(e) => setConfig({ ...config, subject: e.target.value })}
                          placeholder="e.g. Action Required: Application Progress"
                          className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-slate-700 mb-1">Email Body Content (HTML / Text)</label>
                        <textarea
                          rows={4}
                          value={config.bodyHtml || ''}
                          onChange={(e) => setConfig({ ...config, bodyHtml: e.target.value })}
                          placeholder="Enter message body or HTML template..."
                          className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm font-mono text-xs"
                        />
                      </div>
                    </>
                  )}

                  <div>
                    <DocumentTemplateMultiSelect
                      value={Array.isArray(config.documentTemplateIds) ? config.documentTemplateIds : []}
                      onChange={(ids) => setConfig({ ...config, documentTemplateIds: ids })}
                      label="Attach Generated Documents"
                      hint={
                        config.emailTemplateId &&
                        (emailTemplates.find((t) => t.id === config.emailTemplateId)?.documentTemplateIds?.length || 0) > 0
                          ? 'Documents attached to the email template itself are always included when the automation runs — this selection adds more.'
                          : "Generated from the applicant this automation runs on, saved to their Generated Documents, and attached when the email is sent."
                      }
                    />
                  </div>
                {/* I-8: delivery options the engine has always honored
                    but the builder never showed — what the admin sees
                    is now the whole configuration. */}
                <div className="rounded-xl border border-slate-200 bg-slate-50 px-3.5 py-3 space-y-3">
                  <p className="text-[11px] font-bold text-slate-600 uppercase tracking-wider">Delivery Options</p>
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1">
                      Recipient Override (optional)
                    </label>
                    <input
                      type="text"
                      value={config.recipientEmail || ''}
                      onChange={(e) => setConfig({ ...config, recipientEmail: e.target.value })}
                      placeholder="Leave empty to email the applicant"
                      className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm bg-white"
                    />
                    <p className="text-[11px] text-slate-500 mt-1">
                      When set, the email goes to this fixed address instead of the applicant — the
                      applicant's details are still merged into the subject and body. Publishing warns
                      about this so it is never set by accident.
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-slate-700 mb-1">CC (comma separated)</label>
                      <input
                        type="text"
                        value={Array.isArray(config.ccEmails) ? config.ccEmails.join(', ') : ''}
                        onChange={(e) =>
                          setConfig({
                            ...config,
                            ccEmails: e.target.value.split(',').map((t) => t.trim()).filter(Boolean),
                          })
                        }
                        placeholder="e.g. team@example.com"
                        className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm bg-white"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-700 mb-1">BCC (comma separated)</label>
                      <input
                        type="text"
                        value={Array.isArray(config.bccEmails) ? config.bccEmails.join(', ') : ''}
                        onChange={(e) =>
                          setConfig({
                            ...config,
                            bccEmails: e.target.value.split(',').map((t) => t.trim()).filter(Boolean),
                          })
                        }
                        placeholder="e.g. records@example.com"
                        className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm bg-white"
                      />
                    </div>
                  </div>
                </div>
              </div>
            );
          })()}

            {actionType === 'create_event' && (
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    Event Title <span className="text-rose-600">*</span>
                  </label>
                  <input
                    type="text"
                    value={config.eventTitle || ''}
                    onChange={(e) => setConfig({ ...config, eventTitle: e.target.value })}
                    placeholder="e.g. Orientation Interview"
                    className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm font-semibold text-slate-800 focus:ring-2 focus:ring-brand-primary/20 focus:border-indigo-500 outline-none"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    Event Details / Description
                  </label>
                  <textarea
                    rows={3}
                    value={config.eventDescription || ''}
                    onChange={(e) => setConfig({ ...config, eventDescription: e.target.value })}
                    placeholder="Enter event agenda, preparation instructions, or additional details..."
                    className="w-full px-3 py-2 rounded-xl border border-slate-200 text-xs font-medium text-slate-800 focus:ring-2 focus:ring-brand-primary/20 focus:border-indigo-500 outline-none"
                  />
                </div>
                {(() => {
                  const eventWhenMode =
                    config.eventStartMode ||
                    (config.eventDateTime ? 'fixed' : config.eventStartInValue ? 'relative' : 'unscheduled');
                  return (
                    <>
                      <div>
                        <label className="block text-xs font-semibold text-slate-700 mb-1">
                          When
                        </label>
                        <select
                          value={eventWhenMode}
                          onChange={(e) =>
                            setConfig({
                              ...config,
                              eventStartMode: e.target.value,
                              ...(e.target.value === 'relative'
                                ? { eventStartInUnit: config.eventStartInUnit || 'days' }
                                : {}),
                            })
                          }
                          className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm font-semibold text-slate-800 focus:ring-2 focus:ring-brand-primary/20 focus:border-indigo-500 outline-none"
                        >
                          <option value="fixed">On a specific date &amp; time</option>
                          <option value="relative">Relative to when this step runs</option>
                          <option value="unscheduled">No set time — schedule it manually later</option>
                        </select>
                      </div>
                      {eventWhenMode === 'fixed' && (
                        <div>
                          <label className="block text-xs font-semibold text-slate-700 mb-1">
                            Event Date &amp; Time <span className="text-rose-600">*</span>
                          </label>
                          <input
                            type="datetime-local"
                            value={config.eventDateTime ? String(config.eventDateTime).slice(0, 16) : ''}
                            onChange={(e) =>
                              setConfig({
                                ...config,
                                eventStartMode: 'fixed',
                                eventDateTime: e.target.value ? new Date(e.target.value).toISOString() : '',
                              })
                            }
                            className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm font-semibold text-slate-800 focus:ring-2 focus:ring-brand-primary/20 focus:border-indigo-500 outline-none"
                          />
                          <p className="text-[11px] text-slate-500 mt-1">
                            Select both date and exact time for the scheduled event.
                          </p>
                        </div>
                      )}
                      {eventWhenMode === 'relative' && (
                        <div className="flex items-center gap-3">
                          <input
                            type="number"
                            min="1"
                            value={config.eventStartInValue || ''}
                            onChange={(e) =>
                              setConfig({
                                ...config,
                                eventStartMode: 'relative',
                                eventStartInValue: parseInt(e.target.value) || 0,
                                eventStartInUnit: config.eventStartInUnit || 'days',
                              })
                            }
                            placeholder="e.g. 2"
                            className="w-28 px-3 py-2 rounded-xl border border-slate-200 text-sm font-semibold text-slate-800 focus:ring-2 focus:ring-brand-primary/20 focus:border-indigo-500 outline-none"
                          />
                          <select
                            value={config.eventStartInUnit || 'days'}
                            onChange={(e) =>
                              setConfig({ ...config, eventStartMode: 'relative', eventStartInUnit: e.target.value })
                            }
                            className="px-3 py-2 rounded-xl border border-slate-200 text-sm font-semibold text-slate-800 focus:ring-2 focus:ring-brand-primary/20 focus:border-indigo-500 outline-none"
                          >
                            <option value="minutes">Minutes</option>
                            <option value="hours">Hours</option>
                            <option value="days">Days</option>
                          </select>
                          <span className="text-xs text-slate-500 font-medium">after this step runs</span>
                        </div>
                      )}
                      {eventWhenMode === 'unscheduled' && (
                        <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 font-medium">
                          The event will be created with no date or time and flagged as needing scheduling — it will not appear on a calendar until someone sets its date.
                        </p>
                      )}
                    </>
                  );
                })()}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1">Duration (Minutes)</label>
                    <input
                      type="number"
                      value={config.durationMinutes || 30}
                      onChange={(e) => setConfig({ ...config, durationMinutes: parseInt(e.target.value) || 30 })}
                      className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm font-semibold text-slate-800 focus:ring-2 focus:ring-brand-primary/20 focus:border-indigo-500 outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1">Location / Video Link</label>
                    <input
                      type="text"
                      value={config.locationUrl || ''}
                      onChange={(e) => setConfig({ ...config, locationUrl: e.target.value })}
                      placeholder="e.g. https://meet.jit.si/isats-session"
                      className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm font-semibold text-slate-800 focus:ring-2 focus:ring-brand-primary/20 focus:border-indigo-500 outline-none"
                    />
                  </div>
                </div>
              </div>
            )}

            {actionType === 'create_invoice' && (
              <div className="space-y-3">
                <div className="rounded-xl border border-slate-200 bg-slate-50 px-3.5 py-2.5">
                  <p className="text-xs font-semibold text-slate-700">Target Applicant</p>
                  <p className="text-[11px] text-slate-500 leading-snug">
                    The applicant this automation runs on — resolved at run time. Finance's invoice builder
                    picks an applicant explicitly; an automation always issues to its run's applicant.
                  </p>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    Invoice Template (Finance) <span className="text-rose-600">*</span>
                  </label>
                  <SearchableSelect
                    value={config.invoiceTemplateId || ''}
                    onChange={(v) => setConfig({ ...config, invoiceTemplateId: v })}
                    options={invoiceTemplates.map((t: any) => ({
                      value: t.id,
                      label: `${t.code ? `[${t.code}] ` : ''}${t.name}`,
                    }))}
                    placeholder="Search invoice templates..."
                    clearLabel="Select Portal Invoice Template..."
                    emptyLabel="No invoice templates found"
                  />
                  <p className="text-[11px] text-slate-500 mt-1">
                    The same templates Finance's invoice and quote builders use — the invoice is built from
                    the template's items, totals, and terms. A template is required: hand-typed amounts are
                    no longer offered for invoice steps.
                  </p>
                </div>
                {invoiceIsLegacyManual && (
                  <div className="rounded-xl border border-amber-200 bg-amber-50 px-3.5 py-2.5 space-y-1.5">
                    <p className="text-xs font-bold text-amber-900">Legacy manual setup</p>
                    <p className="text-[11px] text-amber-800 leading-snug">
                      This step was saved with hand-entered line items before invoice steps moved to
                      templates. It still runs exactly as saved — nothing here changes that. To convert
                      it, choose a Finance template above; the manual items stay saved on the step but
                      the template will be used at run time.
                    </p>
                    <ul className="text-[11px] text-amber-900 font-medium space-y-0.5">
                      {(config.lineItems || []).map((it: any, i: number) => (
                        <li key={i}>
                          {it.description || 'Item'} — {it.quantity || 1} × ${Number(it.unitPrice ?? it.amount ?? 0).toFixed(2)}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">Invoice Title (Optional Override)</label>
                  <input
                    type="text"
                    value={config.invoiceTitle || ''}
                    onChange={(e) => setConfig({ ...config, invoiceTitle: e.target.value })}
                    placeholder="e.g. Program Enrolment Fee"
                    className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">Due In (Days)</label>
                  <input
                    type="number"
                    value={config.dueInDays || 14}
                    onChange={(e) => setConfig({ ...config, dueInDays: parseInt(e.target.value) || 14 })}
                    className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm"
                  />
                </div>
                <div className="flex items-center gap-2 pt-1">
                  <input
                    type="checkbox"
                    id="sendInvoiceEmail"
                    checked={config.sendInvoiceEmail !== false}
                    onChange={(e) => setConfig({ ...config, sendInvoiceEmail: e.target.checked })}
                    className="rounded text-indigo-600 focus:ring-brand-primary h-4 w-4"
                  />
                  <label htmlFor="sendInvoiceEmail" className="text-xs font-medium text-slate-700">
                    Automatically email invoice to applicant (with the Finance PDF attached, when a template is selected)
                  </label>
                </div>
              </div>
            )}

            {actionType === 'create_quote' && (
              <div className="space-y-3">
                <div className="rounded-xl border border-slate-200 bg-slate-50 px-3.5 py-2.5">
                  <p className="text-xs font-semibold text-slate-700">Target Applicant</p>
                  <p className="text-[11px] text-slate-500 leading-snug">
                    The applicant this automation runs on — resolved at run time. Finance's quote builder
                    picks an applicant explicitly; an automation always issues to its run's applicant.
                  </p>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    Quote Template (Finance) <span className="text-rose-600">*</span>
                  </label>
                  <SearchableSelect
                    value={config.quoteTemplateId || config.invoiceTemplateId || ''}
                    onChange={(v) =>
                      setConfig({ ...config, quoteTemplateId: v, invoiceTemplateId: undefined })
                    }
                    options={invoiceTemplates.map((t: any) => ({
                      value: t.id,
                      label: `${t.code ? `[${t.code}] ` : ''}${t.name}`,
                    }))}
                    placeholder="Search quote templates..."
                    clearLabel="Select Portal Quote Template..."
                    emptyLabel="No quote templates found"
                  />
                  <p className="text-[11px] text-slate-500 mt-1">
                    Finance uses one template list for quotes and invoices — the quote is built from the
                    template's items, totals, and terms. A template is required: hand-typed totals are no
                    longer offered for quote steps.
                  </p>
                </div>
                {quoteIsLegacyManual && (
                  <div className="rounded-xl border border-amber-200 bg-amber-50 px-3.5 py-2.5 space-y-1.5">
                    <p className="text-xs font-bold text-amber-900">Legacy manual setup</p>
                    <p className="text-[11px] text-amber-800 leading-snug">
                      This step was saved with hand-entered line items before quote steps moved to
                      templates. It still runs exactly as saved — nothing here changes that. To convert
                      it, choose a Finance template above; the manual items stay saved on the step but
                      the template will be used at run time.
                    </p>
                    <ul className="text-[11px] text-amber-900 font-medium space-y-0.5">
                      {(config.quoteLineItems || []).map((it: any, i: number) => (
                        <li key={i}>
                          {it.description || 'Item'} — {it.quantity || 1} × ${Number(it.unitPrice ?? it.amount ?? 0).toFixed(2)}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">Quote Title</label>
                  <input
                    type="text"
                    value={config.quoteTitle || ''}
                    onChange={(e) => setConfig({ ...config, quoteTitle: e.target.value })}
                    placeholder="e.g. Program Cost Estimate"
                    className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">Valid For (Days)</label>
                  <input
                    type="number"
                    value={config.validityDays || 30}
                    onChange={(e) => setConfig({ ...config, validityDays: parseInt(e.target.value) || 30 })}
                    className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm"
                  />
                </div>
                <div className="flex items-center gap-2 pt-1">
                  <input
                    type="checkbox"
                    id="sendQuoteEmail"
                    checked={config.sendQuoteEmail !== false}
                    onChange={(e) => setConfig({ ...config, sendQuoteEmail: e.target.checked })}
                    className="rounded text-indigo-600 focus:ring-brand-primary h-4 w-4"
                  />
                  <label htmlFor="sendQuoteEmail" className="text-xs font-medium text-slate-700">
                    Automatically email quote to applicant (with the Finance PDF attached, when a template is selected)
                  </label>
                </div>
              </div>
            )}

            {actionType === 'create_support_ticket' && (
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    Ticket Subject <span className="text-rose-600">*</span>
                  </label>
                  <input
                    type="text"
                    value={config.ticketTitle || ''}
                    onChange={(e) => setConfig({ ...config, ticketTitle: e.target.value })}
                    placeholder="e.g. Automated Document Review Ticket"
                    className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm font-semibold text-slate-800 focus:ring-2 focus:ring-brand-primary/20 focus:border-indigo-500 outline-none"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    Body / Ticket Details
                  </label>
                  <textarea
                    rows={3}
                    value={config.ticketDescription || ''}
                    onChange={(e) => setConfig({ ...config, ticketDescription: e.target.value })}
                    placeholder="Explain the issue, request, or reason for creating this support ticket..."
                    className="w-full px-3 py-2 rounded-xl border border-slate-200 text-xs font-medium text-slate-800 focus:ring-2 focus:ring-brand-primary/20 focus:border-indigo-500 outline-none"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1">Priority</label>
                    <select
                      value={config.ticketPriority || 'normal'}
                      onChange={(e) => setConfig({ ...config, ticketPriority: e.target.value })}
                      className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm font-semibold text-slate-800 focus:ring-2 focus:ring-brand-primary/20 focus:border-indigo-500 outline-none"
                    >
                      <option value="low">Low</option>
                      <option value="normal">Normal</option>
                      <option value="high">High</option>
                      <option value="urgent">Urgent</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1">Category</label>
                    <select
                      value={config.ticketCategory || 'General'}
                      onChange={(e) => setConfig({ ...config, ticketCategory: e.target.value })}
                      className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm font-semibold text-slate-800 focus:ring-2 focus:ring-brand-primary/20 focus:border-indigo-500 outline-none"
                    >
                      <option value="General">General</option>
                      <option value="Document">Document Review</option>
                      <option value="Billing">Billing &amp; Finance</option>
                      <option value="Technical">Technical Support</option>
                      <option value="Urgent">Urgent Escalation</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    Assign to Team Member
                  </label>
                  <SearchableSelect
                    value={config.assignedTeamMemberUid || config.assignedToUid || ''}
                    onChange={(v) => setConfig({ ...config, assignedTeamMemberUid: v, assignedToUid: v })}
                    options={teamMembers.map((t) => ({ value: t.id, label: t.name }))}
                    placeholder="Search internal team members..."
                    clearLabel="Unassigned (General Queue)"
                    emptyLabel="No internal team members found"
                  />
                  <p className="text-[11px] text-slate-500 mt-1">
                    Only administrators and internal team members are listed for ticket assignment.
                  </p>
                </div>
              </div>
            )}

            {actionType === 'create_internal_task' && (
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    Task Title <span className="text-rose-600">*</span>
                  </label>
                  <input
                    type="text"
                    value={config.taskTitle || ''}
                    onChange={(e) => setConfig({ ...config, taskTitle: e.target.value })}
                    placeholder="e.g. Verify Applicant Passport Details"
                    className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm font-semibold text-slate-800 focus:ring-2 focus:ring-brand-primary/20 focus:border-indigo-500 outline-none"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    Task Details / Description
                  </label>
                  <textarea
                    rows={3}
                    value={config.taskDescription || ''}
                    onChange={(e) => setConfig({ ...config, taskDescription: e.target.value })}
                    placeholder="Specify the work to be completed, relevant instructions, and task requirements..."
                    className="w-full px-3 py-2 rounded-xl border border-slate-200 text-xs font-medium text-slate-800 focus:ring-2 focus:ring-brand-primary/20 focus:border-indigo-500 outline-none"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1">Due In (Hours)</label>
                    <input
                      type="number"
                      value={config.dueInHours || 24}
                      onChange={(e) => setConfig({ ...config, dueInHours: parseInt(e.target.value) || 24 })}
                      className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm font-semibold text-slate-800 focus:ring-2 focus:ring-brand-primary/20 focus:border-indigo-500 outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1">Priority</label>
                    <select
                      value={config.taskPriority || 'medium'}
                      onChange={(e) => setConfig({ ...config, taskPriority: e.target.value })}
                      className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm font-semibold text-slate-800 focus:ring-2 focus:ring-brand-primary/20 focus:border-indigo-500 outline-none"
                    >
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                      <option value="urgent">Urgent</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    Assign to Team Member
                  </label>
                  <SearchableSelect
                    value={config.assignedToUid || ''}
                    onChange={(v) => setConfig({ ...config, assignedToUid: v })}
                    options={teamMembers.map((t) => ({ value: t.id, label: t.name }))}
                    placeholder="Search internal team members..."
                    clearLabel="Unassigned"
                    emptyLabel="No internal team members found"
                  />
                  <p className="text-[11px] text-slate-500 mt-1">
                    Only administrators and internal team members are listed for task assignment.
                  </p>
                </div>
              </div>
            )}

            {actionType === 'tag_application' && (
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    Tags used in this workspace
                  </label>
                  {availableTags.length > 0 ? (
                    <div className="flex flex-wrap gap-1.5 rounded-xl border border-slate-200 bg-slate-50/60 p-2.5 max-h-36 overflow-y-auto">
                      {availableTags.map((tag) => {
                        const selected = dedupeTagsCi(coerceTagArray(config.tagsToAdd)).some(
                          (t) => t.toLowerCase() === tag.toLowerCase()
                        );
                        return (
                          <button
                            key={tag}
                            type="button"
                            aria-pressed={selected}
                            onClick={() => {
                              // Selecting an already-selected tag is a
                              // no-op by design: duplicates are
                              // impossible. Unselect via the chip in
                              // "Tags to Add" below.
                              if (selected) return;
                              setConfig({
                                ...config,
                                tagsToAdd: dedupeTagsCi([...coerceTagArray(config.tagsToAdd), tag]),
                              });
                            }}
                            className={`px-2.5 py-1 rounded-full border text-[11px] font-semibold transition-colors ${
                              selected
                                ? 'bg-brand-primary border-brand-primary text-white cursor-default'
                                : 'bg-white border-slate-300 text-slate-700 hover:border-indigo-400 hover:text-indigo-700'
                            }`}
                          >
                            {tag}
                          </button>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-[11px] text-slate-500">
                      No tags are in use on this workspace's applications yet — type a new tag below to create the first one.
                    </p>
                  )}
                  <p className="text-[11px] text-slate-500 mt-1">
                    Click a tag to add it to "Tags to Add" below. Selected tags appear there as chips, where they can be removed.
                  </p>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    Tags to Add
                  </label>
                  <TagChipInput
                    tags={
                      Array.isArray(config.tagsToAdd)
                        ? config.tagsToAdd
                        : typeof config.tagsToAdd === 'string'
                          ? config.tagsToAdd.split(',').map((t) => t.trim()).filter(Boolean)
                          : []
                    }
                    onChange={(tags) => setConfig({ ...config, tagsToAdd: dedupeTagsCi(tags) })}
                    placeholder="Type a tag to add and press comma (,) or Enter..."
                    badgeBgColor="bg-indigo-50 border-indigo-200 text-indigo-700"
                  />
                  <p className="text-[11px] text-slate-500 mt-1">
                    Type or paste tags. Separate tags with a comma (,) or press Enter. Click a tag chip to edit or '×' to remove.
                  </p>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    Tags to Remove
                  </label>
                  <TagChipInput
                    tags={
                      Array.isArray(config.tagsToRemove)
                        ? config.tagsToRemove
                        : typeof config.tagsToRemove === 'string'
                          ? config.tagsToRemove.split(',').map((t) => t.trim()).filter(Boolean)
                          : []
                    }
                    onChange={(tags) => setConfig({ ...config, tagsToRemove: dedupeTagsCi(tags) })}
                    placeholder="Type a tag to remove and press comma (,) or Enter..."
                    badgeBgColor="bg-rose-50 border-rose-200 text-rose-700"
                  />
                  <p className="text-[11px] text-slate-500 mt-1">
                    Matching tag chips will be stripped from the application when this step runs.
                  </p>
                </div>
              </div>
            )}

            {actionType === 'change_workflow_stage' && (
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">Target Workflow Stage</label>
                  <select
                    value={config.targetStageKey === 'processing' ? 'readyForProcessing' : config.targetStageKey || ''}
                    onChange={(e) => setConfig({ ...config, targetStageKey: e.target.value })}
                    className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm"
                  >
                    <option value="">Select Destination Stage...</option>
                    <option value="draft">1. Draft</option>
                    <option value="submitted">2. Submitted for Review</option>
                    <option value="readyForScreening">3. Ready for Screening</option>
                    <option value="readyForProcessing">4. Ready for Processing</option>
                    <option value="billings">5. Billings</option>
                    <option value="immigration">6. Immigration</option>
                    <option value="preDeparture">7. Pre-Departure</option>
                    <option value="closed">8. Closure</option>
                  </select>
                </div>
                {/* I-1: the deliberate gate bypass, stated where the
                    step is configured — not discovered after the fact. */}
                <div className="rounded-xl border border-amber-200 bg-amber-50 px-3.5 py-2.5">
                  <div className="flex items-start gap-2.5">
                    <AlertTriangle className="h-4 w-4 text-amber-700 shrink-0 mt-0.5" />
                    <div>
                      <p className="text-xs font-bold text-amber-900">Deliberate move — stage gates are not checked</p>
                      <p className="text-[11px] text-amber-800 leading-snug">
                        This step moves the applicant straight to the chosen stage without checking that
                        stage's prerequisites or gates — the same deliberate move an admin makes manually in
                        the workflow drawer. Every move is recorded in the applicant's history, attributed to
                        this automation. Publishing this automation repeats this warning.
                      </p>
                    </div>
                  </div>
                </div>
                {config.targetStageKey === 'closed' && (
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1">Closure Type (required)</label>
                    <select
                      value={config.closureType || ''}
                      onChange={(e) => setConfig({ ...config, closureType: e.target.value })}
                      className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm"
                    >
                      <option value="">Select Closure Type...</option>
                      <option value="SD">SD — Service Delivered</option>
                      <option value="SC">SC — Service Cancelled</option>
                      <option value="SP">SP — Service Paused</option>
                    </select>
                    <p className="text-[11px] text-slate-500 mt-1">
                      The automation will not run for an applicant unless a closure type is chosen — the portal never assumes one.
                    </p>
                  </div>
                )}
              </div>
            )}

            {actionType === 'update_application_status' && (
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">Target Application Status</label>
                  <select
                    value={config.targetApplicationStatus || ''}
                    onChange={(e) => setConfig({ ...config, targetApplicationStatus: e.target.value })}
                    className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm outline-none focus:ring-2 focus:ring-brand/20"
                  >
                    <option value="">Select Target Status...</option>
                    <option value="Draft">Draft</option>
                    <option value="Submitted">Submitted</option>
                    <option value="Returned">Returned</option>
                    <option value="Cancelled">Cancelled</option>
                    <option value="Applicant Cancelled">Applicant Cancelled</option>
                    <option value="Closed">Closed</option>
                    <option value="Archived">Archived</option>
                  </select>
                  <p className="text-[11px] text-slate-500 mt-1">
                    Sets the application&apos;s main status — the same change an admin makes from the applicant record, with the same side effects. These are the portal&apos;s canonical statuses.
                  </p>
                </div>

                <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 space-y-2">
                  <label className="flex items-center gap-2 text-xs font-semibold text-slate-700 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={config.sendNotificationEmail !== false}
                      onChange={(e) => setConfig({ ...config, sendNotificationEmail: e.target.checked })}
                      className="rounded border-slate-300 text-brand focus:ring-brand"
                    />
                    <span>Send the status notification email to the applicant</span>
                  </label>
                  <p className="text-[11px] text-slate-500 leading-relaxed pl-6">
                    When enabled, the status change sends the same notification email as the manual status change: the approval email for Submitted, the return email for Returned, the cancellation email for Cancelled, and the archive email for Archived. Draft, Applicant Cancelled and Closed have no notification email. If the email fails, the status change still stands and the failure is recorded on the run.
                  </p>
                </div>
              </div>
            )}

            {(actionType === 'update_batch' || actionType === 'add_to_batch') && (
              <div className="space-y-3">
                {config.batchOperation === 'remove_member' ? (
                  <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5">
                    <p className="text-xs font-semibold text-slate-700">No target batch needed</p>
                    <p className="text-[11px] text-slate-500 mt-0.5">
                      Remove Member takes no batch selection: when the step runs, the applicant is
                      removed from every batch in this workspace that currently contains them.
                    </p>
                  </div>
                ) : (
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1">Target Batch Group</label>
                    <SearchableSelect
                      value={config.batchId || ''}
                      onChange={(v) =>
                        setConfig({
                          ...config,
                          batchId: v,
                          // Keep the saved display name in step with the
                          // selection (the canvas chain label and the
                          // dry-run report read config.batchName).
                          batchName: batches.find((b) => b.id === v)?.name || '',
                        })
                      }
                      options={batches.map((b) => ({ value: b.id, label: b.name }))}
                      placeholder="Search batch groups..."
                      clearLabel="Select Batch Group..."
                      emptyLabel="No batch groups found"
                    />
                  </div>
                )}
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">Operation</label>
                  <select
                    value={config.batchOperation || 'append'}
                    onChange={(e) =>
                      setConfig({
                        ...config,
                        batchOperation: e.target.value as 'append' | 'replace_all' | 'remove_member',
                      })
                    }
                    className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm"
                  >
                    <option value="append">Append — add the selected applicants, keep existing members</option>
                    <option value="replace_all">Replace All — remove existing members, replace with the selected applicants</option>
                    <option value="remove_member">Remove Member — remove the applicant from every batch they belong to</option>
                  </select>
                  <p className="text-[11px] text-slate-500 mt-1">
                    {config.batchOperation === 'replace_all'
                      ? 'Replace All removes everyone currently in the batch and replaces them with the applicants selected for this execution. It is refused while the batch is processing, and it never touches any other batch.'
                      : config.batchOperation === 'remove_member'
                      ? 'Remove Member removes the applicant from every batch in this workspace they are currently a member of. If they belong to no batch, the step succeeds and changes nothing.'
                      : 'Append adds the selected applicants to the batch and keeps everyone already in it. An applicant is never added twice.'}
                  </p>
                </div>
              </div>
            )}

            {actionType === 'update_admin' && (
              <div className="space-y-3">
                <p className="text-[11px] text-slate-500">
                  This step acts on the <strong>workspace team</strong> — this workspace's own staff
                  administrators. Referrer, sponsor and employer teams are separate systems and are
                  never affected. When the step runs, the portal re-checks that the person who
                  started the run still has permission to manage the workspace team, and protected
                  accounts (the workspace owner, platform administrators, the person who started
                  the run, and the last remaining admin) can never be removed, demoted or disabled.
                </p>
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">Workspace Team Administrator</label>
                  {workspaceAdmins.length > 0 ? (
                    <SearchableSelect
                      value={config.adminTargetUid || ''}
                      onChange={(v) => {
                        const picked = workspaceAdmins.find((a) => a.uid === v);
                        setConfig({
                          ...config,
                          adminTargetUid: v,
                          adminTargetName: picked?.name || '',
                          // A removal confirmed for one person never
                          // carries over to another.
                          adminRemovalConfirmed: false,
                        });
                      }}
                      options={workspaceAdmins.map((a) => ({
                        value: a.uid,
                        label: `${a.name}${a.role ? ` (${a.role})` : ''}${a.email ? ` — ${a.email}` : ''}`,
                      }))}
                      placeholder="Search workspace team..."
                      clearLabel="Select administrator..."
                      emptyLabel="No workspace team administrators found"
                    />
                  ) : (
                    <p className="text-[11px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2">
                      The workspace team list could not be loaded. Listing and managing workspace
                      team administrators requires Settings access (workspace admins and owners).
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">Operation</label>
                  <select
                    value={config.adminOperation || 'set_to'}
                    onChange={(e) => {
                      const op = e.target.value as 'append' | 'set_to' | 'remove_admin';
                      setConfig({
                        ...config,
                        adminOperation: op,
                        adminRemovalConfirmed: false,
                        ...(op === 'append'
                          ? { adminField: 'screeningStaff', adminValue: true }
                          : op === 'set_to'
                            ? {
                                adminField: config.adminField || 'displayName',
                                adminValue:
                                  config.adminField === 'status'
                                    ? config.adminValue || 'active'
                                    : config.adminField === 'screeningStaff'
                                      ? config.adminValue ?? true
                                      : config.adminValue ?? '',
                              }
                            : {}),
                      });
                    }}
                    className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm"
                  >
                    <option value="append">Append — add to the screening staff assignment, keeping existing assignments</option>
                    <option value="set_to">Set To — replace one field's value</option>
                    <option value="remove_admin">Remove Admin — remove this person from the workspace team</option>
                  </select>
                </div>

                {(config.adminOperation || 'set_to') === 'append' && (
                  <p className="text-[11px] text-slate-500">
                    Append adds the selected administrator to the workspace's screening staff
                    assignment. Anyone already assigned stays assigned, and nobody is added twice.
                  </p>
                )}

                {(config.adminOperation || 'set_to') === 'set_to' && (
                  <>
                    <div>
                      <label className="block text-xs font-semibold text-slate-700 mb-1">Field</label>
                      <select
                        value={config.adminField || 'displayName'}
                        onChange={(e) => {
                          const f = e.target.value;
                          setConfig({
                            ...config,
                            adminField: f,
                            adminValue:
                              f === 'status' ? 'active' : f === 'screeningStaff' ? true : '',
                          });
                        }}
                        className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm"
                      >
                        <option value="displayName">Display Name</option>
                        <option value="firstName">First Name</option>
                        <option value="lastName">Last Name</option>
                        <option value="role">Workspace Role</option>
                        <option value="status">Status (active / disabled)</option>
                        <option value="screeningStaff">Screening Staff Assignment</option>
                      </select>
                      <p className="text-[11px] text-slate-500 mt-1">
                        Only these fields can be changed. Email, passwords, billing and platform
                        settings are never touched by this action.
                      </p>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-700 mb-1">New Value</label>
                      {(config.adminField || 'displayName') === 'role' ? (
                        <select
                          value={typeof config.adminValue === 'string' ? config.adminValue : ''}
                          onChange={(e) => setConfig({ ...config, adminValue: e.target.value })}
                          className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm"
                        >
                          <option value="">Select a role...</option>
                          {(assignableRoles.length > 0
                            ? assignableRoles
                            : ['admin', 'manager', 'member', 'viewer']
                          ).map((r) => (
                            <option key={r} value={r}>
                              {r}
                            </option>
                          ))}
                        </select>
                      ) : (config.adminField || 'displayName') === 'status' ? (
                        <select
                          value={config.adminValue === 'disabled' ? 'disabled' : 'active'}
                          onChange={(e) => setConfig({ ...config, adminValue: e.target.value })}
                          className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm"
                        >
                          <option value="active">Active</option>
                          <option value="disabled">Disabled</option>
                        </select>
                      ) : (config.adminField || 'displayName') === 'screeningStaff' ? (
                        <select
                          value={config.adminValue === false ? 'false' : 'true'}
                          onChange={(e) => setConfig({ ...config, adminValue: e.target.value === 'true' })}
                          className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm"
                        >
                          <option value="true">Assigned as screening staff</option>
                          <option value="false">Not assigned as screening staff</option>
                        </select>
                      ) : (
                        <input
                          type="text"
                          value={typeof config.adminValue === 'string' ? config.adminValue : ''}
                          onChange={(e) => setConfig({ ...config, adminValue: e.target.value })}
                          className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm"
                          placeholder="New value"
                        />
                      )}
                    </div>
                  </>
                )}

                {(config.adminOperation || 'set_to') === 'remove_admin' && (
                  <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 space-y-2">
                    <p className="text-xs font-bold text-rose-800 flex items-center gap-1.5">
                      <AlertTriangle size={14} /> Remove from the workspace team
                    </p>
                    <p className="text-[11px] text-rose-700">
                      The administrator loses access to this workspace as soon as the step runs.
                      Their account is <strong>not</strong> deleted, and anything they were assigned
                      is unassigned. Saving a Remove Admin step requires Settings edit access, and
                      the run re-checks permissions and protections when it executes.
                    </p>
                    {config.adminRemovalConfirmed === true ? (
                      <p className="text-[11px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
                        Removal confirmed for {config.adminTargetName || config.adminTargetUid || 'the selected administrator'}.
                      </p>
                    ) : (
                      <button
                        type="button"
                        disabled={!config.adminTargetUid}
                        onClick={() => setRemovalConfirmOpen(true)}
                        className="px-3 py-2 rounded-lg text-[11px] font-extrabold text-white bg-rose-600 hover:bg-rose-700 disabled:opacity-50 transition-colors"
                      >
                        Review and confirm removal...
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Save refusal (e.g. a finance step with no template) */}
        {saveError && (
          <div className="mx-6 mb-4 flex items-start gap-2 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-xs font-semibold text-rose-700 shrink-0">
            <span>{saveError}</span>
          </div>
        )}

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-100 flex items-center justify-end gap-3 bg-slate-50 shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-xl text-xs font-bold text-slate-600 hover:bg-slate-200/60 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="px-5 py-2 rounded-xl text-xs font-extrabold text-white bg-brand-primary hover:bg-brand-primary-dark transition-colors shadow-sm"
          >
            Save Action Node
          </button>
        </div>
      </div>

      {/* Slice C: the removal confirmation dialog — the ONLY way the
          adminRemovalConfirmed flag gets set. It names the exact
          administrator being removed; changing the target afterwards
          clears the flag (see the target picker's onChange). */}
      {removalConfirmOpen && (
        <div className="fixed inset-0 z-[9600] flex items-center justify-center bg-slate-900/70 p-4">
          <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-md overflow-hidden">
            <div className="px-5 py-4 border-b border-rose-100 bg-rose-50">
              <h4 className="text-sm font-extrabold text-rose-900 flex items-center gap-2">
                <AlertTriangle size={16} /> Confirm removal from the workspace team
              </h4>
            </div>
            <div className="px-5 py-4 space-y-3">
              <p className="text-sm text-slate-700">
                Remove{' '}
                <strong>
                  {workspaceAdmins.find((a) => a.uid === config.adminTargetUid)?.name ||
                    config.adminTargetName ||
                    config.adminTargetUid}
                </strong>
                {(() => {
                  const picked = workspaceAdmins.find((a) => a.uid === config.adminTargetUid);
                  return picked?.email ? ` (${picked.email})` : '';
                })()}{' '}
                from this workspace's team?
              </p>
              <p className="text-xs text-slate-500">
                When this step runs, they immediately lose access to this workspace and anything
                assigned to them here is unassigned. Their account is not deleted. The workspace
                owner, platform administrators, the person who started the run and the last
                remaining admin can never be removed — the run refuses those with the reason
                recorded.
              </p>
            </div>
            <div className="px-5 py-4 border-t border-slate-100 flex items-center justify-end gap-3 bg-slate-50">
              <button
                type="button"
                onClick={() => setRemovalConfirmOpen(false)}
                className="px-4 py-2 rounded-xl text-xs font-bold text-slate-600 hover:bg-slate-200/60 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  setConfig({ ...config, adminRemovalConfirmed: true });
                  setRemovalConfirmOpen(false);
                }}
                className="px-4 py-2 rounded-xl text-xs font-extrabold text-white bg-rose-600 hover:bg-rose-700 transition-colors"
              >
                Yes, remove this administrator
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
